// Binary Search — Basic vs Optimized code for every "Beginner Problems" drill listed in the
// Binary Search Explanation modal. Same shape as stackDrills.js: concise steps + an interactive
// "watch it happen" visualizer per version. Two Advanced drills cross-reference the curated
// problems instead of duplicating content, mirroring arraysDrills.js's Kadane's stub.

const binarySearchDrills = [
  {
    id: 'classic-binary-search',
    title: 'Search for a Target in a Sorted Array',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Linear Scan',
        explanation: 'Check every element from the start until the target is found (or the array runs out). Correct on any array, sorted or not — but it never uses the fact that the array IS sorted.',
        steps: [
          'Walk the array from index 0.',
          'Compare each element to the target.',
          'Return the index the moment a match is found.',
          'In the worst case (the target is last, or absent), every element gets checked — O(n).',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 7)', values: [1, 3, 5, 7, 9, 11] }],
          varNames: [],
          steps: [
            { highlights: { arr: [0] }, note: '1 ≠ 7 — keep scanning.' },
            { highlights: { arr: [1] }, note: '3 ≠ 7 — keep scanning.' },
            { highlights: { arr: [2] }, note: '5 ≠ 7 — keep scanning.' },
            { highlights: { arr: [3] }, note: '7 = 7 — found at index 3, after 4 comparisons.' },
          ],
        },
        code:
`public int searchBasic(int[] arr, int target) {
    for (int i = 0; i < arr.length; i++) {
        if (arr[i] == target) return i;
    }
    return -1;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Binary Search',
        explanation: 'Because the array is sorted, checking the MIDDLE element tells you which entire half to discard — the target can\'t be in the half that\'s all wrong-direction. Repeating this halves the search space every single step.',
        steps: [
          'Set lo = 0 and hi = arr.length − 1.',
          'While lo ≤ hi, check the middle element.',
          'If it\'s the target, return its index. If it\'s too small, discard the left half (lo = mid + 1). If too big, discard the right half (hi = mid − 1).',
          'Repeat until found, or lo crosses hi (not present).',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 7)', values: [1, 3, 5, 7, 9, 11] }],
          varNames: [],
          steps: [
            { highlights: { arr: [2] }, note: 'lo=0, hi=5, mid=2: arr[2]=5 < 7 — discard the left half. lo = 3.' },
            { highlights: { arr: [4] }, note: 'lo=3, hi=5, mid=4: arr[4]=9 > 7 — discard the right half. hi = 3.' },
            { highlights: { arr: [3] }, note: 'lo=3, hi=3, mid=3: arr[3]=7 — found, in just 3 comparisons instead of 4 (and the gap widens hugely on bigger arrays).' },
          ],
        },
        code:
`public int search(int[] arr, int target) {
    int lo = 0, hi = arr.length - 1;
    while (lo <= hi) {
        int mid = lo + (hi - lo) / 2;
        if (arr[mid] == target) return mid;
        else if (arr[mid] < target) lo = mid + 1;
        else hi = mid - 1;
    }
    return -1;
}`,
        complexity: { time: 'O(log n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'search-insert-position',
    title: 'Find the Index to Insert a Target Into a Sorted Array',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Linear Scan',
        explanation: 'Walk the array until an element at least as big as the target is found — that\'s where the target belongs. If nothing qualifies, it belongs at the very end.',
        steps: [
          'Walk the array from index 0.',
          'The moment arr[i] ≥ target, that\'s the insertion index.',
          'If the scan finishes with nothing ≥ target, the insertion index is the array\'s length.',
          'Every element up to the insertion point gets checked — O(n).',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 2)', values: [1, 3, 5, 6] }],
          varNames: [],
          steps: [
            { highlights: { arr: [0] }, note: '1 < 2 — keep scanning.' },
            { highlights: { arr: [1] }, note: '3 ≥ 2 — insertion index is 1 (target goes right before this element).' },
          ],
        },
        code:
`public int searchInsertBasic(int[] arr, int target) {
    for (int i = 0; i < arr.length; i++) {
        if (arr[i] >= target) return i;
    }
    return arr.length;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Binary Search for the Lower Bound',
        explanation: 'This is a "find the leftmost position satisfying a condition" search — the condition being arr[i] ≥ target. Binary search narrows straight to that boundary without checking every element before it.',
        steps: [
          'Set lo = 0 and hi = arr.length (note: hi starts one PAST the last index here, not at the last index).',
          'While lo < hi, check the middle element.',
          'If arr[mid] < target, the answer is strictly past mid — lo = mid + 1. Otherwise, mid could BE the answer — hi = mid (not mid − 1).',
          'When lo equals hi, that\'s the insertion index.',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 2)', values: [1, 3, 5, 6] }],
          varNames: [],
          steps: [
            { highlights: { arr: [2] }, note: 'lo=0, hi=4, mid=2: arr[2]=5 ≥ 2 — mid could be the answer, don\'t rule it out. hi = 2.' },
            { highlights: { arr: [1] }, note: 'lo=0, hi=2, mid=1: arr[1]=3 ≥ 2 — hi = 1.' },
            { highlights: { arr: [0] }, note: 'lo=0, hi=1, mid=0: arr[0]=1 < 2 — rule it out entirely. lo = 1. Now lo equals hi (1) — insertion index: 1.' },
          ],
        },
        code:
`public int searchInsert(int[] arr, int target) {
    int lo = 0, hi = arr.length;
    while (lo < hi) {
        int mid = lo + (hi - lo) / 2;
        if (arr[mid] < target) lo = mid + 1;
        else hi = mid;
    }
    return lo;
}`,
        complexity: { time: 'O(log n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'first-bad-version',
    title: 'Find the First "Bad" Version Using a Yes/No Check',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Check Every Version in Order',
        explanation: 'Call the isBadVersion(v) check starting from version 1, moving up one at a time, and stop at the first "bad" result. Works, but calls the (often expensive) check far more times than necessary.',
        steps: [
          'Start at version 1.',
          'Call isBadVersion on each version in turn.',
          'Stop at the first version that comes back "bad" — every version before it is guaranteed good.',
          'Up to n calls to isBadVersion in the worst case — O(n).',
        ],
        visual: {
          arrays: [{ label: 'versions (bad from 4 onward)', values: [1, 2, 3, 4, 5] }],
          varNames: [],
          steps: [
            { highlights: { versions: [0] }, note: 'isBadVersion(1): good — keep checking.' },
            { highlights: { versions: [1] }, note: 'isBadVersion(2): good — keep checking.' },
            { highlights: { versions: [2] }, note: 'isBadVersion(3): good — keep checking.' },
            { highlights: { versions: [3] }, note: 'isBadVersion(4): bad! First bad version is 4 — found after 4 calls.' },
          ],
        },
        code:
`public int firstBadVersionBasic(int n) {
    for (int v = 1; v <= n; v++) {
        if (isBadVersion(v)) return v;
    }
    return -1;
}`,
        complexity: { time: 'O(n) calls to isBadVersion', space: 'O(1)' },
      },
      {
        name: 'Optimized — Binary Search on the Boolean Result',
        explanation: 'Once a version is bad, every version after it is bad too — the results form a sorted true/false sequence, even without a literal array. That\'s exactly the shape binary search needs: search for the boundary where the answer flips from "good" to "bad."',
        steps: [
          'Set lo = 1 and hi = n.',
          'While lo < hi, check the middle version.',
          'If it\'s bad, the first bad version is at mid or earlier — hi = mid. If it\'s good, the first bad version is strictly later — lo = mid + 1.',
          'When lo equals hi, that\'s the first bad version — found in O(log n) calls instead of up to n.',
        ],
        visual: {
          arrays: [{ label: 'versions (bad from 4 onward)', values: [1, 2, 3, 4, 5] }],
          varNames: [],
          steps: [
            { highlights: { versions: [2] }, note: 'lo=1, hi=5, mid=3: isBadVersion(3) is good — the first bad one is later. lo = 4.' },
            { highlights: { versions: [3] }, note: 'lo=4, hi=5, mid=4: isBadVersion(4) is bad — it could BE the first bad one. hi = 4. Now lo equals hi (4) — answer: 4, found in only 2 calls.' },
          ],
        },
        code:
`public int firstBadVersion(int n) {
    int lo = 1, hi = n;
    while (lo < hi) {
        int mid = lo + (hi - lo) / 2;
        if (isBadVersion(mid)) hi = mid;
        else lo = mid + 1;
    }
    return lo;
}`,
        complexity: { time: 'O(log n) calls to isBadVersion', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'find-first-last-occurrence',
    title: 'Find the First and Last Position of a Target',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Linear Scan, Tracking Both Ends',
        explanation: 'Walk the whole array once, remembering the first index where the target appears and the last index where it appears. Correct, but scans the entire array even when the target\'s range is found early.',
        steps: [
          'Walk the array from start to end.',
          'The first time the target is seen, record that index as "first."',
          'Every time the target is seen after that, update "last" to that index.',
          'A full O(n) scan runs regardless of where the matches are.',
        ],
        code:
`public int[] searchRangeBasic(int[] arr, int target) {
    int first = -1, last = -1;
    for (int i = 0; i < arr.length; i++) {
        if (arr[i] == target) {
            if (first == -1) first = i;
            last = i;
        }
    }
    return new int[] { first, last };
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Two Binary Searches (Bias Left, Bias Right)',
        explanation: 'Run binary search twice with a small twist: once biased to keep narrowing LEFT even after finding a match (to find the first occurrence), and once biased to keep narrowing RIGHT after a match (to find the last). Each search is still O(log n), so both together are still O(log n).',
        steps: [
          'First pass: on a match, record it but keep searching the LEFT half (hi = mid − 1) for an earlier one.',
          'Second pass: on a match, record it but keep searching the RIGHT half (lo = mid + 1) for a later one.',
          'Neither pass stops at the first match it sees — both deliberately keep going past it.',
          'Two O(log n) searches, run independently.',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 8)', values: [5, 7, 7, 8, 8, 10] }],
          varNames: [],
          steps: [
            { highlights: { arr: [4] }, note: 'Pass 1 (find first): lo=0,hi=5,mid=2: arr[2]=7 < 8 — lo = 3.', phase: 'Find first' },
            { highlights: { arr: [3] }, note: 'lo=3,hi=5,mid=4: arr[4]=8 matches — record first=4, but keep searching left. hi = 3.', phase: 'Find first' },
            { highlights: { arr: [3] }, note: 'lo=3,hi=3,mid=3: arr[3]=8 matches — record first=3, keep searching left. hi=2. Now lo(3) > hi(2) — first occurrence: index 3.', phase: 'Find first' },
            { highlights: { arr: [4] }, note: 'Pass 2 (find last): lo=0,hi=5,mid=2: arr[2]=7 < 8 — lo = 3.', phase: 'Find last' },
            { highlights: { arr: [4] }, note: 'lo=3,hi=5,mid=4: arr[4]=8 matches — record last=4, keep searching right. lo = 5.', phase: 'Find last' },
            { highlights: { arr: [5] }, note: 'lo=5,hi=5,mid=5: arr[5]=10 > 8 — hi=4. Now lo(5) > hi(4) — last occurrence: index 4. Final answer: [3, 4].', phase: 'Find last' },
          ],
        },
        code:
`public int[] searchRange(int[] arr, int target) {
    return new int[] { findBound(arr, target, true), findBound(arr, target, false) };
}

private int findBound(int[] arr, int target, boolean findFirst) {
    int lo = 0, hi = arr.length - 1, result = -1;
    while (lo <= hi) {
        int mid = lo + (hi - lo) / 2;
        if (arr[mid] == target) {
            result = mid;
            if (findFirst) hi = mid - 1; else lo = mid + 1;
        } else if (arr[mid] < target) {
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return result;
}`,
        complexity: { time: 'O(log n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'find-peak-element',
    title: 'Find a Peak Element (Any Local Maximum)',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Check Every Element\'s Neighbors',
        explanation: 'For each position, check whether it\'s bigger than both neighbors (treating the array\'s edges as bordered by −∞). Correct, but examines every element even though the array being "peaky" gives a shortcut.',
        steps: [
          'For each index, compare it to its left and right neighbor.',
          'The moment one is bigger than both (or an edge case at either end), that\'s a peak — return it.',
          'Every index gets checked in the worst case — O(n).',
        ],
        code:
`public int findPeakBasic(int[] arr) {
    for (int i = 0; i < arr.length; i++) {
        boolean leftOk = (i == 0) || arr[i - 1] < arr[i];
        boolean rightOk = (i == arr.length - 1) || arr[i] > arr[i + 1];
        if (leftOk && rightOk) return i;
    }
    return -1;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Binary Search Using the Slope',
        explanation: 'At any middle index, comparing it to its right neighbor tells you which direction definitely still contains a peak: if the slope is going UP (arr[mid] < arr[mid+1]), a peak must exist somewhere to the right (the array trends upward until it can\'t anymore). If the slope is going down or flat, a peak is at mid or to its left.',
        steps: [
          'Set lo = 0 and hi = arr.length − 1.',
          'While lo < hi, compare the middle element to its right neighbor.',
          'If arr[mid] < arr[mid + 1], a peak is guaranteed somewhere to the right — lo = mid + 1.',
          'Otherwise, a peak is at mid or to its left — hi = mid. When lo equals hi, that\'s a peak.',
        ],
        visual: {
          arrays: [{ label: 'arr', values: [1, 2, 3, 1] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [1], secondary: [2] } }, note: 'lo=0, hi=3, mid=1: arr[1]=2 < arr[2]=3 — climbing uphill, a peak must be to the right. lo = 2.' },
            { highlights: { arr: { primary: [2], secondary: [3] } }, note: 'lo=2, hi=3, mid=2: arr[2]=3 > arr[3]=1 — going downhill from here, the peak is at mid or earlier. hi = 2. Now lo equals hi (2) — peak found at index 2 (value 3).' },
          ],
        },
        code:
`public int findPeak(int[] arr) {
    int lo = 0, hi = arr.length - 1;
    while (lo < hi) {
        int mid = lo + (hi - lo) / 2;
        if (arr[mid] < arr[mid + 1]) {
            lo = mid + 1;
        } else {
            hi = mid;
        }
    }
    return lo;
}`,
        complexity: { time: 'O(log n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'sqrt-x',
    title: 'Integer Square Root of a Number',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Count Upward Until Overshooting',
        explanation: 'Starting from 0, keep squaring the next integer until it exceeds n — the previous integer was the answer. Only takes √n steps (not n), but still leaves an O(log n) improvement on the table.',
        steps: [
          'Start i at 0.',
          'While (i+1)² does not exceed n, advance i.',
          'The moment (i+1)² would overshoot, i itself is the floor of the square root.',
          'Runs in O(√n) steps — much better than a full O(n) scan, but still not the fastest option.',
        ],
        visual: {
          arrays: [{ label: 'candidates i = 0, 1, 2, …', values: [0, 1, 2, 3, 4, 5, 6] }],
          varNames: [],
          steps: [
            { highlights: { candidates: [4] }, note: '5² = 25 ≤ 27 — keep going.' },
            { highlights: { candidates: [5] }, note: '6² = 36 > 27 — overshoot. The previous value, 5, is floor(√27).' },
          ],
        },
        code:
`public int mySqrtBasic(int n) {
    int i = 0;
    while ((long) (i + 1) * (i + 1) <= n) {
        i++;
    }
    return i;
}`,
        complexity: { time: 'O(√n)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Binary Search on the Answer',
        explanation: 'The answer lives somewhere in the range [0, n], and "is mid² ≤ n?" is a question whose answer flips exactly once as mid increases — from true to false. That\'s a binary-searchable boundary, collapsing the O(√n) walk into O(log n).',
        steps: [
          'Set lo = 0 and hi = n.',
          'Check mid² against n at each step.',
          'If mid² ≤ n, mid could be the answer (or there\'s a bigger valid one) — remember it, then search higher: lo = mid + 1.',
          'If mid² > n, search lower: hi = mid − 1. The last remembered value is floor(√n).',
        ],
        visual: {
          arrays: [{ label: 'search range [0, n] for n = 27', values: [0, 27] }],
          varNames: ['lo', 'hi', 'ans'],
          steps: [
            { vars: { lo: 0, hi: 27, ans: 0 }, note: 'mid = 13: 13² = 169 > 27 — too big, hi = 12.' },
            { vars: { lo: 0, hi: 12, ans: 0 }, note: 'mid = 6: 36 > 27 — too big, hi = 5.' },
            { vars: { lo: 0, hi: 5, ans: 2 }, note: 'mid = 2: 4 ≤ 27 — remember ans=2, search higher, lo = 3.' },
            { vars: { lo: 3, hi: 5, ans: 4 }, note: 'mid = 4: 16 ≤ 27 — remember ans=4, lo = 5.' },
            { vars: { lo: 5, hi: 5, ans: 5 }, note: 'mid = 5: 25 ≤ 27 — remember ans=5, lo = 6. Now lo > hi — final answer: floor(√27) = 5.' },
          ],
        },
        code:
`public int mySqrt(int n) {
    int lo = 0, hi = n, ans = 0;
    while (lo <= hi) {
        int mid = lo + (hi - lo) / 2;
        long square = (long) mid * mid;
        if (square <= n) {
            ans = mid;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return ans;
}`,
        complexity: { time: 'O(log n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'search-rotated-sorted-array-drill',
    title: 'Search in a Rotated Sorted Array',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Search in Rotated Sorted Array\" there for the complete step-by-step and both code versions. You can " +
      "also try it hands-on in the \"Rotated Sorted Array\" mode of the Interactive Practice playground with " +
      "any rotated array and target of your choosing.",
  },
  {
    id: 'koko-eating-bananas',
    title: 'Minimum Eating Speed to Finish in Time (Binary Search on the Answer)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Try Every Speed From 1 Upward',
        explanation: 'Try eating speeds starting at 1, computing how many hours each speed would take (rounding each pile up, since a partial pile still costs a full hour), and stop at the first speed that fits within the time limit. Correct, but tries speeds one at a time from the very bottom.',
        steps: [
          'Try speed k = 1, 2, 3, … in order.',
          'For each speed, compute total hours: sum of ceil(pile / k) for every pile.',
          'The first speed whose total hours fits within the limit is the answer.',
          'Trying every speed up to the true answer, each costing an O(n) hour-count, adds up fast.',
        ],
        code:
`public int minEatingSpeedBasic(int[] piles, int h) {
    int maxPile = Arrays.stream(piles).max().getAsInt();
    for (int k = 1; k <= maxPile; k++) {
        if (hoursNeeded(piles, k) <= h) return k;
    }
    return maxPile;
}

private long hoursNeeded(int[] piles, int k) {
    long hours = 0;
    for (int pile : piles) hours += (pile + k - 1) / k;  // ceiling division
    return hours;
}`,
        complexity: { time: 'O(maxPile · n)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Binary Search Over the Possible Speeds',
        explanation: 'A slower speed always takes MORE OR EQUAL hours than a faster one — the relationship between speed and hours-needed is monotonic. That means "is this speed fast enough?" is a binary-searchable yes/no question over the range of possible speeds, just like the sliding-window and boolean-check drills elsewhere in this course.',
        steps: [
          'Set lo = 1 and hi = the largest pile size (no speed faster than that is ever needed).',
          'For the middle speed, compute the total hours needed.',
          'If it fits within the limit, this speed works — remember it as a candidate, then try something slower to save more: hi = mid − 1.',
          'If it doesn\'t fit, this speed is too slow — try faster: lo = mid + 1. The smallest working speed found is the answer.',
        ],
        visual: {
          arrays: [{ label: 'piles (h = 8)', values: [3, 6, 7, 11] }],
          varNames: ['lo', 'hi', 'ans'],
          steps: [
            { vars: { lo: 1, hi: 11, ans: 0 }, note: 'mid = 6: hours = ceil(3/6)+ceil(6/6)+ceil(7/6)+ceil(11/6) = 1+1+2+2 = 6 ≤ 8 — fits! Remember ans=6, try slower: hi = 5.' },
            { vars: { lo: 1, hi: 5, ans: 6 }, note: 'mid = 3: hours = 1+2+3+4 = 10 > 8 — too slow, need faster: lo = 4.' },
            { vars: { lo: 4, hi: 5, ans: 4 }, note: 'mid = 4: hours = 1+2+2+3 = 8 ≤ 8 — fits! Remember ans=4, try slower: hi = 3. Now lo(4) > hi(3) — final answer: minimum speed 4.' },
          ],
        },
        code:
`public int minEatingSpeed(int[] piles, int h) {
    int lo = 1, hi = Arrays.stream(piles).max().getAsInt();
    int ans = hi;
    while (lo <= hi) {
        int mid = lo + (hi - lo) / 2;
        if (hoursNeeded(piles, mid) <= h) {
            ans = mid;
            hi = mid - 1;
        } else {
            lo = mid + 1;
        }
    }
    return ans;
}

private long hoursNeeded(int[] piles, int k) {
    long hours = 0;
    for (int pile : piles) hours += (pile + k - 1) / k;
    return hours;
}`,
        complexity: { time: 'O(n log maxPile)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'median-of-two-sorted-arrays-drill',
    title: 'Median of Two Sorted Arrays',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Median of Two Sorted Arrays\" there for the complete step-by-step and both code versions. It's the " +
      "hardest problem in this pattern group: binary search not over values, but over PARTITION POINTS across " +
      "two arrays at once.",
  },
];

export default binarySearchDrills;
